/** @module FormulatedProduct */

import { featureTogglesStore } from "csi/state/rootStore";

define([
	"dojo/_base/lang",
	"csi/_base/modules",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/api",
	"csi/canvas/storeFactory",
	"csi/comm/PostRequest",
	"csi/config/preferences",
	"csi/locale",
	"csi/page/ComparePageStore",
	"csi/registry/bannerRegistry",
	"csi/registry/pageStoreRegistry",
	"csi/service/rightPaneService",
	"csi/state/rootStore",
	"csi/widget/MessageDialog",
	"csi/xml",
	"csi/view",
	"Specification/BOM/BOMCompareBanner",
	"./FoodLabel/IngredientContributorPaneComponent",
], function(lang, modules, csi, _, crews, api, storeFactory, PostRequest, configPreferences, locale, ComparePageStore, { registerBanner },
	{ registerPageStore }, { INGREDIENT_CONTRIBUTOR }, { localStore, rightPaneStore, routerStore }, MessageDialog, xml, csiView, BOMCompareBanner, IngredientContributorPaneComponent) {

	const ACTUAL_ITEM = "ACTUALITEM";
	const BOM_LINE_BOM = "BOMLINEBOM";
	const BOM_LINE_SKU = "BOMLINESKU";
	const REVISION_STATE_DRAFT = "Revision.State:DRAFT";

	function productPresentationLayoutFoodFunction(node, cache) {
		return storeFactory.getPresentationProductType(node, cache) === "Food";
	}

	storeFactory.addReferenceDataType("ProductPresentationLayout", "Food", productPresentationLayoutFoodFunction);


	// C8-36530: do not allow linking special materials
	api.addLinkValidation("FormulatedMaterial", ({ Special }) => !Special);

	// C8-36530: do not allow linking to Special Formulated Material’s Recipe node name or revision in the RecipeLineItem.
	api.addLinkValidation("RecipeDataSheet", ({ Special }) => !Special);
	api.addLinkValidation("RecipeDataSheetRevision", ({ Special }) => !Special);

	// C8-37705: do not allow linking to FoodLabelDataSheet nor FoodLabelDataSheetRevision when IsRecipeLabel is true.
	api.addLinkValidation("FoodLabelDataSheet", ({ IsRecipeLabel }) => !IsRecipeLabel);
	api.addLinkValidation("FoodLabelDataSheetRevision", ({ IsRecipeLabel }) => !IsRecipeLabel);

	api.addSpecialPath("FormulatedMaterial", "Child:Actual");

	api.addCommentPath("Food", xml.path("ProductSourcing", "ProductSources"));
	api.addCommentPath("Food", xml.path("ProductSourcing", "ProductSources", "Samples"));
	api.addCommentPath("Food", xml.path("ProductSourcing", "ProductSources", "SupplierItems"));

	api.addCommentPath("FormulatedMaterial", xml.path("ProductSourcing", "ProductSources"));
	api.addCommentPath("FormulatedMaterial", xml.path("ProductSourcing", "ProductSources", "Samples"));
	api.addCommentPath("FormulatedMaterial", xml.path("ProductSourcing", "ProductSources", "SupplierItems"));

	/* --- BOM Compare --- */
	/** initialize the banner for BOM Compare  */
	registerBanner("FoodBOM", BOMCompareBanner.default);

	/** initialize the page store for BOM Compare  */
	const _initCompareStore = async (node, revisionNode, cache) => {
		return new ComparePageStore.default(node, revisionNode, cache);
	}

	// register the page store for PP node
	registerPageStore("FoodBOM", _initCompareStore);

	return {

		/**
		 * If the child view exist, refresh the child view
		 * @param {string} childViewId
		 */
		_refreshChildViewIfExists: function (childViewId) {
			const childView = csiView.getView(childViewId);
			if (childView) {
				childView.refreshWidget();
			}
		},

		/**
		 * If any views with the viewId exist, refresh the views
		 * @param {string} viewId 
		 */
		_refreshViewsIfExists: function (viewId) {
			const matchingViews = csiView.getAllViews(viewId);
			if (matchingViews) {
				matchingViews.forEach(view => view.refreshWidget());
			}
		},

		/**
		 * Display the tab only if "EngineeringChange" module is installed and if the feature flag "Enable Impact Analysis" is true
		 * @param {Context} ctx
		 * @param {Tab} defTab
		 * @param {string} rootNodeType
		 * @param {Node?} rootNode
		 */
		onAccessTabImpactAnalysis: function (ctx, defTab, rootNodeType, rootNode) {
			return modules.hasModule("EngineeringChange") && featureTogglesStore.ENABLE_IMPACT_ANALYSIS;
		},

		/**
		 * Display the action only if "IngredientContributor" exists in the available rightPane components
		 */
		onAccessInspectIngredientItem: function() {
			const { locationNode } = routerStore;

			// Has Ingredient Contributor Table
			const showIcon = rightPaneStore
				.getAvailableComponents(locationNode)
				.map((component) => component.id)
				.includes(INGREDIENT_CONTRIBUTOR);

			return showIcon;
		},

		postActionBOMLineItemBOMLineBOM(ac) {
			const lineItem = ac.getNode();
			if (!xml.isEmptyURL(lineItem.BOMLineBOM)) {
				this._checkCircularReference(ac, BOM_LINE_BOM, this._getBOMRevisionState(ac));
			}
			this.postActionBOMLineItemQtyDefault(ac);
			ac.widget.postActionRefreshHierarchyRow(ac);
		},

		postActionBOMLineItemBOMLineSKU(ac) {
			const lineItem = ac.getNode();
			const cache = ac.getCache();
			const skuBOM = cache.getPathNode(lineItem.BOMLineSKU, "Child:SKUFoodBOM");
			if (!xml.isEmptyURL(lineItem.BOMLineBOM) && skuBOM) {
				this._checkCircularReference(ac, BOM_LINE_SKU, this._getSKUBOMRevisionState(ac));
			}
			this.postActionBOMLineItemQtyDefault(ac);
		},

		postActionBOMLineItemActualItem(ac) {
			const lineItem = ac.getNode();
			/**
			 * Checks to determine if we need to do a Circular Reference check,
			 * ActualItem only triggers a Circular Reference check if the FoodBOMLineItem is ItemProductType Food,
			 * the FoodBOM is SKU Centric,
			 * and if the ActualItem (which is a SKU) contains a Default BOM
			 */
			if (!xml.isEmptyURL(lineItem.ActualItem) && lineItem.ItemProductType === "ProductType:Food") {
				const cache = ac.getCache();
				const bomSubtypeSettings = cache.getPathNode(ac.getParent(), "Child:__Parent__/Child:Subtype/Child:Settings");
				const itemsProductTypes = bomSubtypeSettings ? bomSubtypeSettings.ItemsProductTypes : [];
				const bomSelectionPreference = bomSubtypeSettings ? bomSubtypeSettings.ActualItemsSelectionPreference : {};

				if (itemsProductTypes.includes(lineItem.ItemProductType) &&
					bomSelectionPreference[lineItem.ItemProductType] === "ActualItemsSelectionPreference:BaseRealizedProduct") {

					const skuBOM = cache.getPathNode(lineItem.ActualItem, "Child:SKUFoodBOM");
					if (!xml.isEmptyURL(lineItem.BOMLineSKU) && skuBOM) {
						this._checkCircularReference(ac, ACTUAL_ITEM, this._getBOMRevisionState(ac));
					}
				}
			}
			this.postActionBOMLineItemQtyDefault(ac);
		},

		postActionBOMLineItemQtyDefault(ac) {
			const views = csiView.getAllViews("HierarchicalBOMLineItems");

			/* C8FB-1040:
				PostAction was added to BOMLineBOMRevision and ActualItem,
				there is a chance this action is not used for the view FoodBOM Placement views,
				since BOMLineBOMRevision is not in the default view of HierarchicalBOMLineItems
			*/
			if (!views) {
				return;
			}
			views.forEach((v) => {
				const qtyDefault = v.defView.findHeading("QtyDefault", false);
				const totalRecipeQty = v.defView.findHeading("TotalRecipeQty", false);
				const recipeQtyPctOfLevel = v.defView.findHeading("RecipeQtyPctOfLevel", false);
				v.refreshHeading(qtyDefault);
				v.refreshHeading(totalRecipeQty);
				v.refreshHeading(recipeQtyPctOfLevel);
			});
		},

		/** C8FB-2099: After "Calculate Ingredients", refresh the "Ingredient Items" and "FlatIngredientItems" views */
		postActionCalculateIngredients(ac) {
			ac.widget?.postActionRefresh(ac);
			this._refreshChildViewIfExists("FlatIngredientItems");
		},

		/** C8FB-3378 Ingredient Item Contributor Pane should be closed if deletion happens */
		postActionDeleteIngItem(ac) {
			const contributorPane = rightPaneStore.selected;
			if (contributorPane instanceof IngredientContributorPaneComponent && contributorPane.ingredientItemUrl === ac.m_urlNode) {
				rightPaneStore.closeSelected();
			}
		},

		/**	C8-37315 & C8-37752 : After performing an action, if the user is on one of this tabs - Allergen/Ingredient/Nubrient/Claim Items, refresh the current tab. */
		postActionRefreshFoodLabelItemViews: function (ac) {
			this._refreshChildViewIfExists("IngredientItems");
			this._refreshChildViewIfExists("AllergenItems");
			this._refreshChildViewIfExists("NutrientItems");
			this._refreshChildViewIfExists("ClaimItems");
		},

		/**
		 * Ensure that the Selected column in the table of quotes is updated when the ProductionQuote is changed
		 * @param {Controller} ac
		 */
		postActionSelectQuote(ac) {
			ac.widget.getSiblingView("FoodSupplierItems")?.redrawData();
		},

		/** C8FB-12, C8FB-2193: After performing action "UpdateRecipeLabel", if any of these items view displayed, refresh the views.*/
		postActionUpdateRecipeLabel: function (ac) {
			this._refreshViewsIfExists("IngredientItems");
			this._refreshViewsIfExists("FlatIngredientItems");
			this._refreshViewsIfExists("IngredientListGlobal");
			this._refreshViewsIfExists("AllergenItems");
			this._refreshViewsIfExists("NutrientItems");
		},

		/**
		 * C8-37311: Refresh all Recipe Revision Line items headings.
		 * @param {Controller} ac
		 */
		refreshRecipeLineItemsHeadings: function () {
			const views = csiView.getAllViews("Items");

			views.forEach((v) => {
				const amountH = v.defView.findHeading("QtyDefault", false);
				const amountAfterH2OLossH = v.defView.findHeading("AmountAfterH2OLoss", false);
				const h2OLossPctH = v.defView.findHeading("H2OLossPct", false);
				const h2OAmountBeforeLossH = v.defView.findHeading("H2OAmountBeforeLoss", false);
				const h2OAmountAfterLossH = v.defView.findHeading("H2OAfterBeforeLoss", false);
				const pctOfLevelH = v.defView.findHeading("PctOfLevel", false);
				const pctOfLevelAfterH2OLossH = v.defView.findHeading("PctOfLevelAfterH2OLoss", false);
				const pctOfTotalH = v.defView.findHeadingBy((h) => h.m_nameOrig === "PctOfTotal");
				const pctOfTotalAfterH2OLossH = v.defView.findHeadingBy((h) => h.m_nameOrig === "PctOfTotalAfterH2OLoss");

				v.refreshHeading(amountH);
				v.refreshHeading(amountAfterH2OLossH);
				v.refreshHeading(h2OLossPctH);
				v.refreshHeading(h2OAmountAfterLossH);
				v.refreshHeading(h2OAmountBeforeLossH);
				v.refreshHeading(pctOfLevelH);
				v.refreshHeading(pctOfLevelAfterH2OLossH);
				v.refreshHeading(pctOfTotalH);
				v.refreshHeading(pctOfTotalAfterH2OLossH);
			});
		},
		/**
		 * C8-37311: If QtyDefault changes then refresh all the Percentages Headings.
		 * @param {Controller} ac
		 */
		postActionRecipeLineItemAmountAndPctChange: function (ac) {
			this.refreshRecipeLineItemsHeadings();
			this._refreshChildViewIfExists("Properties");
		},
		/**
		 * C8-37311: If Subrecipe is changed then recalculated moisture loss and refresh headings.
		 * @param {Controller} ac
		 */
		postActionRecipeLineItemSubRecipeChange: function (ac) {
			this.refreshMoistureLoss(() => {
				ac.widget.postActionRefreshHierarchyRow(ac);
				this.refreshRecipeLineItemsHeadings();
			});
		},
		/**
		 * C8-37311: If Recipe Line Item is removed then refresh pct's headings.
		 * @param {Controller} ac
		 */
		postActionRecipeLineItemDeleted: function (ac) {
			this.refreshMoistureLoss(() => this.refreshRecipeLineItemsHeadings());
		},
		/**
		 * C8-37311: Refresh Items table and Properties view.
		 * @param {Controller} ac
		 */
		postActionRefreshMoistureLoss: function (ac) {
			ac.widget.refreshDataPreserveHierarchy(ac);
			this._refreshChildViewIfExists("Properties");
		},
		/**
		 * C8-38607: refresh Recipe attributes After Calculate Bathc.
		 * @param {Controller} ac
		 */
		postActionRefreshCalculateBatch: function (ac) {
			ac.widget.postActionRefresh(ac);
			this._refreshChildViewIfExists("Properties");
		},
		/**
		 * C8-37311: Execute Refresh moisture loss server operation.
		 * @param {function} func Post Response funtion to be executed
		 */
		refreshMoistureLoss: function (func) {
			const revisionNode = routerStore.locationRevisionNode;
			if (revisionNode) {
				new PostRequest("FormulatedProduct", "RecipeRefreshH2OLoss")
					.addParameter("ParentUrl", revisionNode.$URL)
					.post({}).then(lang.hitch(this, () => {
						func();
						this._refreshChildViewIfExists("Properties");
					}));
			}
		},

		/**
		 * C8FB-97: Determines if user has write access to the Product's ProductHierarchyDetails ref.
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionResetHierarchy: function (ctx, action) {
			return xml.nodeHasAccess(ctx.getNode(), xml.RIGHT_WRITE, 'ProductHierarchyDetails');
		},

		/**
		 * C8-40302: Determines if user has write access to the FoodSKU node.
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionFoodSKUDataSheets: function (ctx, action) {
			return xml.nodeHasAccess(ctx.getContextNode(), xml.RIGHT_WRITE);;
		},

		/**
		 * C8FB-1717: Determines if user is allowed to perform a new from foodSKU action
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionNewFromFoodSKU: function (ctx, action) {
			const contextNode = ctx.getContextNode();
			const cache = ctx.cache;
			const foodSubtype = cache.get(contextNode.Subtype);
			const projectLineItemToSku = cache.get(foodSubtype.Settings).ProjectLineItemToSKU;
			const skuAdmin = crews.isInRoles(["SKU Administrator"], contextNode);
			const projectEditor = (projectLineItemToSku == "ProjectLineItemToSKU:None") || (crews.isInRoles(["Project Editor", "Project Administrator"], contextNode));
			const foodEditAccess = xml.nodeHasAccess(contextNode, xml.RIGHT_WRITE)

			return skuAdmin && projectEditor && foodEditAccess;
		},

		/**
		 * C8-39407: Determines if there are FoodBOM DataSheets on the Data Package to show the action.
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionSelectDocsFromFoodBOMOnMaterials: function (ctx, action) {
			return this.hasDataSheetOfType(ctx, action, "FoodBOMRevision");
		},
		/**
		 * C8-39407: Determines if there are FoodBOM DataSheets on the Data Package to show the action.
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionSelectDocsFromRecipeFormulatedMaterials: function (ctx, action) {
			return this.hasDataSheetOfType(ctx, action, "RecipeDataSheetRevision");
		},
		/**
		 * C8-36101: Determines if there are DataSheets of a given type on the Data Package to show the action.
		 * @param {Context} ctx
		 * @param {Action} action
		 * @param {String} type
		 */
		hasDataSheetOfType(ctx, action, type) {
			const dataSheets = ctx.cache.getNodes(ctx.getParent().DataSheets);
			let hasFoodBOMs = false;
			dataSheets.forEach(ds => {
				if (!ds) {
					return;
				}
				const sheet = ctx.cache.getNode(ds.Sheet, true);
				if (sheet.$Type === type) {
					hasFoodBOMs = true;
					return;
				}
			});
			return hasFoodBOMs;
		},

		/**
		 * On creating Product on ProductHierarchyDetails, set the newly created Product's ProductHierarchyDetails ref to the curent node
		 */
		onActionProductHierarchyDetailsNewProduct: function (ac) {
			ac.m_writer.changeNode(ac.m_urlNode).changeAttribute("ProductHierarchyDetails", xml.ATTR_REF, ac.m_urlParent);
		},

		/**
		 * C8FB-1086: If BOMLineBOM is set but BOMRevision is not,
		 * then show circular reference warning
		 */
		_checkCircularReference: function (ac, type, revState) {
			const lineItem = ac.getNode();
			if (!xml.isEmptyURL(lineItem.BOMLineBOM) && xml.isEmptyURL(lineItem.BOMLineBOMRevision)) {
				let msg;
				const cache = ac.getCache();
				const bomLineBOMCurrRevision = cache.getPathNode(lineItem.BOMLineBOM, "Child:CurrentRevision");
				const bomLineBOM = cache.getNode(lineItem.BOMLineBOM);
				const parentBOMRev = cache.getNode(lineItem.__Parent__);
				const parentBOM = cache.getNode(parentBOMRev.__Parent__);
				switch (type) {
					case ACTUAL_ITEM:
						const actualItem = cache.getNode(lineItem.ActualItem);
						if (revState === REVISION_STATE_DRAFT) {
							msg = locale.resource("ActualItemCircularReferenceDraft_Warning", [actualItem.$Name, parentBOM.$Name, parentBOMRev.$Name, bomLineBOM.$Name, bomLineBOMCurrRevision.$Name]);
						} else {
							msg = locale.resource("ActualItemCircularReferenceDefault_Warning", [actualItem.$Name, parentBOM.$Name, parentBOMRev.$Name]);
						}
						break;
					case BOM_LINE_BOM:
						if (revState === REVISION_STATE_DRAFT) {
							msg = locale.resource("BOMLineBOMCircularReferenceDraft_Warning", [bomLineBOM.$Name, parentBOM.$Name, parentBOMRev.$Name, bomLineBOMCurrRevision.$Name]);
						} else {
							msg = locale.resource("BOMLineBOMCircularReferenceDefault_Warning", [bomLineBOM.$Name, parentBOM.$Name, parentBOMRev.$Name]);
						}
						break;
					case BOM_LINE_SKU:
						const bomLineSKU = cache.getNode(lineItem.BOMLineSKU);
						const skuBOM = cache.getPathNode(lineItem.BOMLineSKU, "Child:SKUFoodBOM");
						if (revState === REVISION_STATE_DRAFT) {
							const skuBOMRev = cache.getPathNode(skuBOM, "Child:CurrentRevision");
							msg = locale.resource("BOMLineSKUCircularReferenceDraft_Warning", [bomLineSKU.$Name, skuBOM.$Name, parentBOM.$Name, parentBOMRev.$Name, skuBOMRev.$Name]);
						} else {
							msg = locale.resource("BOMLineSKUCircularReferenceDefault_Warning", [bomLineSKU.$Name, skuBOM.$Name, parentBOM.$Name, parentBOMRev.$Name]);
						}
						break;
				}
				this._createCircularReferenceWarning(msg);
			}
		},

		/**
		 * C8FB-1086: If BOMLineBOM is set but BOMRevision is not,
		 * then show circular reference warning
		 */
		_createCircularReferenceWarning: function (msg) {
			let params = {};

			if (params.materialIcon == null) {
				params.materialIcon = "warning";
			}

			if (!params.buttons) {
				params.buttons = [MessageDialog.BUTTON_CLOSE];
			}

			if (!params.title) {
				params.title = "Circular Reference Warning";
			}

			if (params.message == null) {
				params.message = msg;
			}

			new MessageDialog(params).show();
		},

		/**
		 * C8FB-1086: return state of FoodBOMLineItem.BOMLineBOMRevision
		 */
		_getBOMRevisionState(ac) {
			const cache = ac.getCache();
			const lineItem = ac.getNode();
			const currentRevision = cache.getPathNode(lineItem.BOMLineBOM, "Child:CurrentRevision");

			return currentRevision.State;
		},

		/**
		 * C8FB-1086: return state of FoodBOMLineItem.BOMLineBOMRevision
		 */
		_getSKUBOMRevisionState(ac) {
			const cache = ac.getCache();
			const lineItem = ac.getNode();
			const currentRevision = cache.getPathNode(lineItem.BOMLineSKU, "Child:SKUFoodBOM/Child:CurrentRevision");

			return currentRevision.State;
		},

		/**
		 * C8FB-2097: Check if Flat Ingredients View should show
		 */
		_getFlatIngredientToggleStatus(node) {
			return localStore.formulatedProduct.lastToggleStates.get(node);
		}
	};
});
